// src/components/Common/ProductForm.js  
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import CategorySelector from './CategorySelector';
import ProductDescription from './ProductDescription';
import ProductColorSection from './ProductColorSection';
import { useProductData } from '../../hooks/useProductData';
import { useProductOperations } from '../../hooks/useProductOperations';
import { useNumberValidation } from '../../hooks/useNumberValidation';

const ProductForm = ({ product, onClose, onSuccess }) => {
  const isEdit = !!product?.id;
  const emptyProduct = {
    name_ar: '',
    description_ar: '',
    category_id: '',
    price: '',
    new_price: '',
    free_shipping: false,
    colors: []
  };
  
  const [formData, setFormData] = useState(emptyProduct);
  const [activeColorIndex, setActiveColorIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [hasDiscount, setHasDiscount] = useState(false);
  const [hasFreeShipping, setHasFreeShipping] = useState(false);
  
  const { loadProductData } = useProductData();
  const { createProduct, updateProduct } = useProductOperations();
  const { validateNumberInput } = useNumberValidation();
  
  // Load existing product data if in edit mode
  useEffect(() => {
    if (product?.id) {
      loadProductData(product.id, setFormData, setLoading);
    }
  }, [product, loadProductData]);

  // Separate effect to check discount and free shipping after formData is loaded
  useEffect(() => {
    if (isEdit && formData.new_price) {
      // Check if product has discount (new_price exists and is greater than 0)
      if (parseFloat(formData.new_price) > 0) {
        setHasDiscount(true);
      }
    }
    
    if (isEdit && formData.free_shipping) {
      setHasFreeShipping(true);
    }
  }, [formData.new_price, formData.free_shipping, isEdit]);
  
  const rtlTextFieldProps = {
    inputProps: {
      dir: 'rtl',
      fontWeight: 'bold',
      style: { 
        textAlign: 'right',
        paddingRight: '10px',
      },
    },
    InputLabelProps: {
      sx: {
        textAlign: 'right',
        right: 30,
        left: 'auto',
        fontWeight: 'bold',
        transformOrigin: 'top right'
      },
    },
    sx: {
      '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        transform: 'translate(0, -9px) scale(0.75)',
        right: 20,
      },
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        marginRight: '6px',
      },
      '& .MuiInputBase-input': {
        paddingLeft: '14px',
      },
    },
  };

  const handlePriceChange = (field, value) => {
    const validatedValue = validateNumberInput(value, true); // Allow decimals for price fields
    setFormData({ ...formData, [field]: validatedValue });
  };

  const handleDiscountToggle = (event) => {
    const isChecked = event.target.checked;
    setHasDiscount(isChecked);
    
    if (!isChecked) {
      // Clear new_price when discount is disabled
      setFormData({ ...formData, new_price: '' });
    }
  };

  const handleFreeShippingToggle = (event) => {
    const isChecked = event.target.checked;
    setHasFreeShipping(isChecked);
    setFormData({ ...formData, free_shipping: isChecked });
  };

  const handleAddColor = () => {
    const newColor = { 
      color_id: '', 
      images: [], 
      sizes: [],
      color_order: formData.colors.length,
      is_home_color: formData.colors.length === 0 // First color is home color by default
    };
    setFormData({ ...formData, colors: [...formData.colors, newColor] });
  };

  const handleColorChange = (idx, colorId) => {
    const colorsCopy = [...formData.colors];
    colorsCopy[idx].color_id = colorId;
    setFormData({ ...formData, colors: colorsCopy });
  };

  const handleRemoveColor = (idx) => {
    const colorsCopy = [...formData.colors];
    const removedColor = colorsCopy[idx];
    colorsCopy.splice(idx, 1);
    
    // Update color orders
    colorsCopy.forEach((color, index) => {
      color.color_order = index;
    });
    
    // If removed color was home color, make first color the home color
    if (removedColor.is_home_color && colorsCopy.length > 0) {
      colorsCopy[0].is_home_color = true;
    }
    
    setFormData({ ...formData, colors: colorsCopy });
  };

  const handleColorUpdate = (idx, updatedColor) => {
    const colorsCopy = [...formData.colors];
    colorsCopy[idx] = updatedColor;
    setFormData({ ...formData, colors: colorsCopy });
  };

  const handleColorReorder = (fromIndex, toIndex) => {
    const colorsCopy = [...formData.colors];
    const [moved] = colorsCopy.splice(fromIndex, 1);
    colorsCopy.splice(toIndex, 0, moved);
    
    // Update color orders
    colorsCopy.forEach((color, index) => {
      color.color_order = index;
    });
    
    setFormData({ ...formData, colors: colorsCopy });
  };

  const handleHomeColorChange = (colorIndex, isHome) => {
    const colorsCopy = [...formData.colors];
    
    if (isHome) {
      // Set all colors as not home, then set the selected one as home
      colorsCopy.forEach(color => {
        color.is_home_color = false;
      });
      colorsCopy[colorIndex].is_home_color = true;
    } else {
      // Can't unset home color if it's the only one
      const homeColorsCount = colorsCopy.filter(c => c.is_home_color).length;
      if (homeColorsCount === 1) {
        setValidationError('يجب أن يكون هناك لون رئيسي واحد على الأقل');
        return;
      }
      colorsCopy[colorIndex].is_home_color = false;
    }
    
    setFormData({ ...formData, colors: colorsCopy });
    setValidationError('');
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.index === destination.index) return;
    
    handleColorReorder(source.index, destination.index);
  };

  const validateForm = () => {
    if (!formData.name_ar.trim()) {
      setValidationError('اسم المنتج مطلوب');
      return false;
    }
    
    if (!formData.category_id) {
      setValidationError('فئة المنتج مطلوبة');
      return false;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      setValidationError('السعر مطلوب ويجب أن يكون أكبر من صفر');
      return false;
    }

    // Validate discount price if discount is enabled
    if (hasDiscount) {
      if (!formData.new_price || parseFloat(formData.new_price) <= 0) {
        setValidationError('السعر بعد الخصم مطلوب ويجب أن يكون أكبر من صفر');
        return false;
      }
      
      if (parseFloat(formData.new_price) >= parseFloat(formData.price)) {
        setValidationError('السعر بعد الخصم يجب أن يكون أقل من السعر الأساسي');
        return false;
      }
    }
    
    if (formData.colors.length === 0) {
      setValidationError('يجب إضافة لون واحد على الأقل');
      return false;
    }
    
    // Check if there's at least one home color
    const hasHomeColor = formData.colors.some(color => color.is_home_color);
    if (!hasHomeColor) {
      setValidationError('يجب تحديد لون رئيسي واحد على الأقل');
      return false;
    }
    
    // Check if all colors have valid color_id
    const invalidColors = formData.colors.filter(color => !color.color_id);
    if (invalidColors.length > 0) {
      setValidationError('يجب اختيار لون لجميع الألوان المضافة');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  const handleSaveProduct = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Prepare form data - pricing and free shipping is handled at product level
      const productData = {
        ...formData,
        // Clear new_price if discount is not enabled
        new_price: hasDiscount ? formData.new_price : '',
        // Set free_shipping based on checkbox state
        free_shipping: hasFreeShipping
      };
      
      if (isEdit) {
        await updateProduct(productData);
      } else {
        await createProduct(productData);
      }
      onSuccess();
    } catch (error) {
      console.error(`${isEdit ? 'Update' : 'Create'} product error:`, error);
      setValidationError(`خطأ في ${isEdit ? 'تحديث' : 'إنشاء'} المنتج: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFormData({ ...formData, category_id: categoryId });
  };

  const handleDescriptionChange = (description) => {
    setFormData({ ...formData, description_ar: description });
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'right', direction: 'rtl' }} dir="rtl">
        {isEdit ? 'تعديل المنتج' : 'إضافة منتج جديد'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <Typography>جاري تحميل البيانات...</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="اسم المنتج"
                value={formData.name_ar}
                onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                {...rtlTextFieldProps}
              />
            </Grid>
            
            <Grid item xs={12}>
              <CategorySelector 
                value={formData.category_id} 
                onChange={handleCategoryChange} 
                rtlTextFieldProps={rtlTextFieldProps} 
              />
            </Grid>

            {/* Checkboxes Section - positioned before price fields */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasFreeShipping}
                      onChange={handleFreeShippingToggle}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                  }
                  label="شحن مجاني"
                  sx={{ 
                    direction: 'rtl',
                    fontWeight: 'bold',
                    '& .MuiFormControlLabel-label': {
                      fontWeight: 'bold',
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasDiscount}
                      onChange={handleDiscountToggle}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    />
                  }
                  label="إضافة خصم"
                  sx={{ 
                    direction: 'rtl',
                    fontWeight: 'bold',
                    '& .MuiFormControlLabel-label': {
                      fontWeight: 'bold',
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Discount Price Field - Only show if discount is enabled */}
            {hasDiscount && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="السعر بعد الخصم"
                  type="number"
                  value={formData.new_price || ''}
                  onChange={e => handlePriceChange('new_price', e.target.value)}
                  inputProps={{
                    ...rtlTextFieldProps.inputProps,
                    min: "0",
                    step: "0.01"
                  }}
                  InputLabelProps={{
                    ...rtlTextFieldProps.InputLabelProps,
                    required: false // Remove the asterisk
                  }}
                  sx={{
                    ...rtlTextFieldProps.sx,
                    '& .MuiInputLabel-asterisk': {
                      display: 'none' // Hide asterisk if it appears
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                    }
                  }}
                />
              </Grid>
            )}

            {/* Price Section - Both fields on the same line */}
            <Grid item xs={hasDiscount ? 6 : 12}>
              <TextField
                fullWidth
                label="السعر"
                type="number"
                value={formData.price}
                onChange={e => handlePriceChange('price', e.target.value)}
                inputProps={{
                  ...rtlTextFieldProps.inputProps,
                  min: "0",
                  step: "0.01"
                }}
                InputLabelProps={{
                  ...rtlTextFieldProps.InputLabelProps,
                  required: false // Remove the asterisk
                }}
                sx={{
                  ...rtlTextFieldProps.sx,
                  '& .MuiInputLabel-asterisk': {
                    display: 'none' // Hide asterisk if it appears
                  }
                }}
              />
            </Grid>
            
            <ProductDescription
              value={formData.description_ar}
              onChange={handleDescriptionChange}
              rtlTextFieldProps={rtlTextFieldProps}
            />

            {/* Validation Error Alert */}
            {validationError && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setValidationError('')}>
                  {validationError}
                </Alert>
              </Grid>
            )}

            {/* Colors Section with Drag and Drop */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'right', direction: 'rtl', width: '100%' }}>
                الألوان ({formData.colors.length})
              </Typography>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="colors">
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {formData.colors.map((color, index) => (
                        <Draggable key={`color-${index}`} draggableId={`color-${index}`} index={index}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                                transition: 'transform 0.2s ease',
                                mb: 1
                              }}
                            >
                              <ProductColorSection
                                color={color}
                                colorIndex={index}
                                activeColorIndex={activeColorIndex}
                                setActiveColorIndex={setActiveColorIndex}
                                onColorChange={handleColorChange}
                                onRemoveColor={handleRemoveColor}
                                onColorUpdate={handleColorUpdate}
                                onColorReorder={handleColorReorder}
                                onHomeColorChange={handleHomeColorChange}
                                totalColors={formData.colors.length}
                                rtlTextFieldProps={rtlTextFieldProps}
                                hidePrice={true} // Hide price fields from color sections
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>

            <Grid item xs={12}>
              <Button 
                variant="outlined" 
                endIcon={<Add />} 
                onClick={handleAddColor} 
                sx={{ fontWeight: 'bold' }}
              >
                إضافة لون
              </Button>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 'bold' }}>
          إلغاء
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSaveProduct}
          disabled={loading || !formData.category_id || !formData.name_ar || !formData.price}
          sx={{ fontWeight: 'bold' }}
        >
          {isEdit ? 'تحديث المنتج' : 'إضافة المنتج'}
        </Button>
      </DialogActions>
    </>
  );
};

export default ProductForm;
